import { EditOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import {
  Badge,
  Button,
  Col,
  Collapse,
  Descriptions,
  Divider,
  Card as AntdCard,
  Drawer,
  Modal,
  Row,
  Select,
  Spin,
  Tag,
  Tooltip,
  message,
  Card
} from "antd";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import moment from "moment";
import React, { Fragment, RefObject } from "react";
import { RouteComponentProps } from "react-router";
import { Link, NavLink } from "react-router-dom";
import AttachmentsDisplay from "../components/AttachmentsDisplay";
import AttachmentsSection, {
  UNASSIGN_ATTACHMENT_FROM_TRANSFER_REQUEST,
} from "../components/AttachmentsSection";
import CardSection from "../components/CardSection";
import BaseForm from "../components/Form/BaseForm";
import ModalCreator from "../components/ModalCreator";
import NotesSection from "../components/NotesSection";
import {
  ASSIGN_ATTACHMENT_TO_TRANSFER_REQUEST,
  ASSIGN_NOTE_TO_TRANSFER_REQUEST,
  FOLLOW_UP_TRANSFER_REQUEST,
  GET_TRANSFER_REQUEST,
  GET_TRANSFER_REQUEST_LOGS,
  GET_TRANSFER_REQUEST_NOTIFIER_RECORDS_QUERY,
  GET_TRANSFER_REQUEST_REASONS,
  MOVE_TRANSFER_REQUEST_TO_STATE,
  UPDATE_CUSTOMER_FIRST_CONTRIBUTION,
} from "../gql/transfer-requests";
import { GET_TEMPLATES_BY_CATEGORY_NAME } from "../gql/templates";
import {
  ActiveDrawerContent,
  APIError,
  Attachment,
  Changelog,
  Customer,
  Entitlement,
  FormFieldConfig,
  Note,
  NotificationRecord,
  RedemptionsRequest,
  RequestReason,
  SelectOptionsInterface,
  Subscription,
  TransferRequest,
  TransferRequestAttachment,
  TransferRequestNote,
  TransitionToNextState,
  User,
} from "../helpers/shared-interfaces";
import {
  capitalizeFirstLetter,
  checkPermission,
  formatSnakeCase,
  generateDisplayErrorMessage,
  generateNameFromCustomer,
  generateStateButtonText,
  handleValidationErrors,
  request,
  responseHandler,
  spaceCamel,
  transferTypes,
  getStateColor
} from "../helpers/utilities";
import { CREATE_ATTACHMENT, CREATE_NOTE } from "../gql/general";
import entitlements from "../config/entitlements";
import { PaymentAccountCard } from "../components/PaymentsAccountsView";
import { tabPaneStyle } from "../helpers/constants";
import { RequestReasonUI } from "./ViewRedemptionRequest";
import { notificationColumns, NotificationRecordTableFactory } from "./NotificationRecords";
import RequestChangeLogs from "../components/RequestChangeLogs";

const { Panel } = Collapse;

interface State {
  id: string | number;
  isLoading: boolean;
  showReasonModal: boolean;
  isReasonLoading: boolean;
  selectedTransitionState?: string;
  showConfirmActionModal: boolean;
  isConfirmActionLoading: boolean;
  transferRequest?: TransferRequest;
  notes: Note[];
  currentNote?: Note;
  attachments: Attachment[];
  transferRequestAttachments: TransferRequestAttachment[];
  transferRequestReasons: RequestReason[];
  attachmentIds: string[];
  requestLogs: Changelog[];
  showDrawer: boolean;
  showFirstContributionModal: boolean;
  currentRequestsToProcess: number;
  redemptionRequest?: RedemptionsRequest;
  availableTransitionStates?: TransitionToNextState[]
  commentRequired: boolean
  activeDrawerContent: ActiveDrawerContent;
  showCancelModal: boolean;
  notifications?: NotificationRecord[]
  proofOfContributionDocumentUrl?: string
  showFollowUpModal: boolean;
  followUpTemplateId: string;
  followUpTemplates: Array<{ id: string; name: string }>;
  isFollowUpLoading: boolean;
  isFollowUpTemplatesLoading: boolean;
}

interface RouteParams {
  id: string;
}

interface Props
  extends RouteComponentProps<
    RouteParams,
    any,
    { type: string; requests?: TransferRequest[] }
  > {
  entitlement: Entitlement;
  user: User;
}

export const changelogColumns = [
  {
    title: "Date",
    dataIndex: "createdAt",
    key: "date",
    width: "25%",
    render(text: string): JSX.Element {
      const [date, time] = text.split(" ");
      return (
        <span>
          {moment(date, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YYYY")} at {time}
        </span>
      );
    },
  },
  {
    title: "Status",
    dataIndex: "newValue",
    key: "status",
    align: "left" as const,
    render(text: string): JSX.Element {
      return (
        <Tag
          color={getStateColor(text)}
          style={{ textTransform: "capitalize" }}
        >
          {formatSnakeCase(text)}
        </Tag>
      );
    },
  },
  {
    title: "User",
    dataIndex: "user",
    key: "user",
    align: "left" as const,
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
  {
    title: "Comment",
    dataIndex: "comment",
    key: "comment",
    align: "left" as const,
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
];

export const FirstContributionUI = (props: {
  reference: React.RefObject<FormInstance<any>>;
  initialContributionDate?: string;
  contributionDocumentUrl?: string;
  handleFileUploadChange: (field: string, combinedInfo: string) => void
}): JSX.Element => {
  const fields: FormFieldConfig[] = [
    {
      id: "firstContributionDate",
      name: "firstContributionDate",
      inputType: "defaultDate",
      required: false,
      rule: "object",
      defaultPickerValue: props.initialContributionDate
        ? moment(props.initialContributionDate, "YYYY/MM/DD")
        : undefined,
      label: "First Contribution Date",
      elementStyle: { width: "100%" },
      colConfig: {
        span: 24,
      },
    },
    {
      id: "proofOfContributionDocumentUrl",
      name: "proofOfContributionDocumentUrl",
      label: "Proof of Contribution",
      inputType: "fileUpload",
      placeholder: "Proof of Contribution",
      rule: "string",
      initialValue: props.contributionDocumentUrl,
      changeHandler: (combinedInfo: string) => props.handleFileUploadChange?.("proofOfContributionDocumentUrl", combinedInfo),
      defaultFileList: [
        {
          uid: "1",
          name: props.contributionDocumentUrl?.split("/").pop() || "file",
          status: "done",
          url: props.contributionDocumentUrl,
        },
      ],
      required: false,
      colConfig: {
        span: 24,
      },
    },
  ];

  return (
    <>
      <Spin spinning={false}>
        <Row>
          <Col
            span={24}
            style={{
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                marginTop: "10px",
              }}
            >
              <BaseForm
                layout="vertical"
                reference={props.reference}
                fields={fields}
                fieldsToHide={[]}
              ></BaseForm>
            </div>
          </Col>
        </Row>
      </Spin>
    </>
  );
};

export default class ViewTransferRequests extends React.Component<
  Props,
  State
> {
  reference: RefObject<FormInstance>;
  reasonReference: RefObject<FormInstance>;
  contributionReference: RefObject<FormInstance>;
  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      transferRequest: undefined,
      isLoading: true,
      id: "",
      showReasonModal: false,
      isReasonLoading: false,
      isConfirmActionLoading: false,
      showConfirmActionModal: false,
      showFirstContributionModal: false,
      notes: [],
      currentNote: undefined,
      attachments: [],
      transferRequestAttachments: [],
      transferRequestReasons: [],
      attachmentIds: [],
      requestLogs: [],
      showDrawer: false,
      currentRequestsToProcess: 0,
      commentRequired: false,
      activeDrawerContent: "",
      showCancelModal: false,
      showFollowUpModal: false,
      followUpTemplateId: "",
      followUpTemplates: [],
      isFollowUpLoading: false,
      isFollowUpTemplatesLoading: false,
    };
    this.getTransferRequest = this.getTransferRequest.bind(this);
    this.reference = React.createRef<FormInstance>();
    this.reasonReference = React.createRef<FormInstance>();
    this.contributionReference = React.createRef<FormInstance>();
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  setReasonReference = (ref: React.RefObject<FormInstance>): void => {
    this.reasonReference = ref;
  };

  setContributionReference = (ref: React.RefObject<FormInstance>): void => {
    this.contributionReference = ref;
  };

  getTransferRequestLogs = (): void => {
    const { id } = this.state;
    interface Response {
      transferRequestChangeLogs: Changelog[];
    }

    this.setState({ isLoading: true });
    request({
      query: GET_TRANSFER_REQUEST_LOGS,
      variables: { transferRequestId: id },
    })
      .then(({ data }: AxiosResponse<{ data: Response }>): void => {
        this.setState({ requestLogs: data?.data?.transferRequestChangeLogs });
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  };

  redirectToAllTransferRequestsPage = (): void => {
    this.props.history.push("/transfer-requests");
  };

  getTransferRequest(): void {
    const { id } = this.state;
    interface Response {
      transferRequest: TransferRequest;
    }
    this.setState({ isLoading: true });
    request({
      query: GET_TRANSFER_REQUEST,
      variables: { id },
    })
      .then(({ data }: AxiosResponse<{ data: Response }>): void => {
        this.setState({
          transferRequest: data.data.transferRequest,
          notes: data?.data?.transferRequest?.transferRequestNotes?.map(
            (transferRequestNote: TransferRequestNote) =>
              transferRequestNote.note,
          ),
          attachments:
            data?.data?.transferRequest?.transferRequestAttachments?.map(
              (transferRequestAttachment: TransferRequestAttachment) =>
                transferRequestAttachment.attachment,
            ),
          transferRequestAttachments:
            data?.data?.transferRequest?.transferRequestAttachments,
          availableTransitionStates: data.data.transferRequest?.canTransitionToNextStates,
        });
        this.getTransferRequestLogs();
      })
      .finally((): void => {
        this.setState({ isLoading: false });
      });
  }

  requestForReason = async (state: string): Promise<void> => {
    if (this.state.isReasonLoading) {
      return;
    }

    interface Response {
      transferRequestReasons: RequestReason[];
      errors: APIError[];
    }

    this.setState({ isReasonLoading: true });

    try {
      const { data }: AxiosResponse<{ data: Response }> = await request({
        query: GET_TRANSFER_REQUEST_REASONS,
        variables: { state },
      });

      this.setState({
        transferRequestReasons: data.data.transferRequestReasons,
        showReasonModal: true,
        selectedTransitionState: state,
      });
    } catch (error) {
      message.error(`An error occurred, please try again later - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isReasonLoading: false });
    }
  };

  toggleShowReasonModal = (): void => {
    this.setState((previousState: Readonly<State>) => ({
      showReasonModal: !previousState.showReasonModal,
    }));
  };

  toggleConfirmActionModal = (): void => {
    this.setState((previousState: Readonly<State>) => ({
      showConfirmActionModal: !previousState.showConfirmActionModal,
    }));
  };

  saveStateReason = async (): Promise<void> => {
    if (this.state.isReasonLoading) {
      return;
    }

    const { selectedTransitionState, id, proofOfContributionDocumentUrl } = this.state;

    let data;

    if (!this.reasonReference.current) return;

    const fieldsToBeValidated = ["transferRequestReasonId", "comment", "firstContributionDate"];

    try {
      data = await this.reasonReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    interface Response {
      transferRequest: TransferRequest;
      errors: APIError[];
    }

    this.setState({ isReasonLoading: true });
    const variables = {
      ...data,
      transferRequestId: id,
      state: selectedTransitionState,
      proofOfContributionDocumentUrl
    };
    try {
      const response: AxiosResponse = await request({
        query: MOVE_TRANSFER_REQUEST_TO_STATE,
        variables,
      });

      const { err } = responseHandler<Response>(
        response,
        "moveTransferRequestToState",
      );

      if (err) {
        message.error(formatSnakeCase(generateDisplayErrorMessage(err)));
      } else {
        this.reasonReference.current.resetFields(fieldsToBeValidated);
        message.success("Request updated successfully");
        this.getTransferRequest();
      }
    } catch (error) {
      message.error(`An error occurred, please try again later - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isReasonLoading: false, showReasonModal: false, });
    }
  };

  generateButtonColor = (state: string): string => {
    if (["reversed", "cancelled"].includes(state)) return "error-btn";
    if (["paid", "approved"].includes(state)) return "success-btn";
    return "warning-btn";
  }

  componentDidMount(): void {
    this.setState(
      {
        id: this.props.match.params.id,
        currentRequestsToProcess:
          this.props?.location?.state?.requests?.findIndex(
            (item) =>
              item?.id.toString() === this.props.match.params.id.toString(),
          ) ?? 0,
      },
      (): void => {
        this.getTransferRequest();
      },
    );
  }

  assignNoteToTransferRequest = (noteId: string): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignNoteToTransferRequest: {
          transferRequestNote: {
            note: Note;
          };
        };
      };
    }

    return request({
      query: ASSIGN_NOTE_TO_TRANSFER_REQUEST,
      variables: { noteId: noteId, transferRequestId: this.state.id },
    }).then(({ data: { data } }: AxiosResponse<Response>): void => {
      this.setState({
        isLoading: false,
        currentNote: data.assignNoteToTransferRequest.transferRequestNote.note,
        notes: [
          ...this.state.notes,
          data.assignNoteToTransferRequest.transferRequestNote.note,
        ],
      });
    });
  };

  assignAttachmentToTransferRequest = async (
    attachmentIds: string[],
    transferRequestId: string | number,
  ): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignAttachmentsToTransferRequest: {
          transferRequest: {
            transferRequestAttachments: TransferRequestAttachment[];
          };
        };
      };
    }

    return request({
      query: ASSIGN_ATTACHMENT_TO_TRANSFER_REQUEST,
      variables: {
        attachmentIds,
        transferRequestId,
      },
    }).then(({ data }: AxiosResponse<Response>): void => {
      this.setState({
        isLoading: false,
        attachmentIds: [],
        transferRequestAttachments:
          data.data.assignAttachmentsToTransferRequest.transferRequest
            .transferRequestAttachments,
      });
    });
  };

  createNote = async (): Promise<void> => {
    const { user } = this.props;

    interface Response {
      transferRequestNote: Note;
      note: Note;
    }

    const fieldsToBeValidated: string[] = ["description"];

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_NOTE,
        variables: {
          description: data.description,
          userId: user.id,
        },
      });
      this.setState({ isLoading: false });

      const { mutation } = responseHandler<Response>(response, "createNote");
      this.assignNoteToTransferRequest(mutation.note.id);
      this.reference.current &&
        this.reference.current.resetFields(fieldsToBeValidated);
    } catch (error) {
      Sentry.captureException(error);
      this.setState({ isLoading: false });
    }
  };

  createAttachment = async (combinedInfo: string): Promise<void> => {
    interface Response {
      attachment: Attachment;
    }

    const fieldsToBeValidated: string[] = ["url"];

    this.setState({ isLoading: true });

    try {
      const [url, name] = combinedInfo.split("@@@");

      const response: AxiosResponse = await request({
        query: CREATE_ATTACHMENT,
        variables: {
          url,
          name,
        },
      });
      this.setState({ isLoading: false });

      const { mutation } = responseHandler<Response>(
        response,
        "createAttachment",
      );
      this.setState(
        (state) => ({
          attachmentIds: [...state.attachmentIds, mutation.attachment.id],
          attachments: [...state.attachments, mutation.attachment],
        }),
        () =>
          this.assignAttachmentToTransferRequest(
            this.state.attachmentIds,
            this.state.id,
          ),
      );

      this.reference.current &&
        this.reference.current.resetFields(fieldsToBeValidated);
    } catch (error) {
      Sentry.captureException(error);
      this.setState({ isLoading: false });
    }
  };

  unassignAttachmentToTransferRequest = async (id: string): Promise<void> => {
    const { transferRequestAttachments } = this.state;
    const transferRequestAttachmentToDelete:
      | TransferRequestAttachment
      | undefined = transferRequestAttachments.find(
        (transferRequestAttachment) =>
          transferRequestAttachment.attachment.id === id,
      );
    interface Response {
      result: boolean;
      errors: APIError[];
    }
    try {
      this.setState({ isLoading: true });
      const response: AxiosResponse = await request({
        query: UNASSIGN_ATTACHMENT_FROM_TRANSFER_REQUEST,
        variables: { id: transferRequestAttachmentToDelete?.id },
      });
      const { mutation } = responseHandler<Response>(
        response,
        "unassignAttachmentFromTransferRequest",
      );
      if (!mutation.result) {
        message.error("Failed to delete transfer request");
      } else {
        const attachments = this.state.attachments.filter(
          (attachment) => attachment.id !== id,
        );
        this.setState({
          attachments,
          isLoading: false,
        });
        message.success("Deleted Attachment Successfully");
      }
    } catch (error) {
      message.error(`An error occurred, please refresh and try again later - ${generateDisplayErrorMessage(error)}`);
    }
  };

  getRequestNotifierRecords = (): void => {
    const { id: transferRequestId } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_TRANSFER_REQUEST_NOTIFIER_RECORDS_QUERY,
      variables: { transferRequestId },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            transferRequest: TransferRequest;
          };
        }>): void => {
          this.setState({
            transferRequest: {
              ...this.state.transferRequest,
              ...data.data.transferRequest,
            },
          });
        },
      )
      .finally((): void => {
        this.setState({
          isLoading: false,
        });
      });
  };


  executeDrawerQuery(currentDrawer: ActiveDrawerContent): void {
    switch (currentDrawer) {
      case "notifierRecords":
        this.getRequestNotifierRecords();
        break;
      case "audits":
        this.getTransferRequestLogs();
        break;
      default:
        break;
    }
    return;
  }

  toggleDrawer = (activeDrawerContent: ActiveDrawerContent): (() => void) => {
    return (): void => {
      this.executeDrawerQuery(activeDrawerContent);
      this.setState({
        activeDrawerContent,
        showDrawer: !this.state.showDrawer,
        showCancelModal: false,
      });
    };
  };


  toggleFirstContributionModal = (): void => {
    this.setState(((prevState) => ({
      showFirstContributionModal: !prevState.showFirstContributionModal
    })))
  };

  getSubscription = () => {
    const { transferRequest } = this.state;
    return transferRequest?.customer?.customerSubscriptions?.find(
      (subscription) => {
        return subscription.product.id === transferRequest.toProduct?.id;
      },
    );
  };

  updateCustomerFirstContribution = async (): Promise<void> => {
    const { proofOfContributionDocumentUrl } = this.state

    interface Response {
      customerSubscription: Subscription;
    }

    const fieldsToBeValidated: string[] = [
      "firstContributionDate",
      "proofOfContributionDocumentUrl",
    ];

    if (!this.contributionReference.current) return;

    let data;

    try {
      data = await this.contributionReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });
    const subscription = this.getSubscription();

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_CUSTOMER_FIRST_CONTRIBUTION,
        variables: {
          ...data,
          customerSubscriptionId: subscription?.id,
          proofOfContributionDocumentUrl
        },
      });
      this.setState({ isLoading: false });

      const { err } = responseHandler<Response>(
        response,
        "updateCustomerSubscription",
      );

      if (err) {
        message.error(`Failed to Update First Contribution - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState({ showFirstContributionModal: false });
        message.success("First Contribution Updated Successfully");
      }
    } catch (error) {
      Sentry.captureException(error);
      this.setState({ isLoading: false });
      message.error(`Failed to Update First Contribution - ${generateDisplayErrorMessage(error)}`);
    }
  };

  getDrawerContent = (): JSX.Element => {
    const { requestLogs, activeDrawerContent } =
      this.state;

    switch (activeDrawerContent) {
      case "audits":
        return (
          <RequestChangeLogs data={requestLogs} />
        )
      case "notifierRecords":
        const records = this.state.transferRequest
          ?.notifierRecords as NotificationRecord[];

        return (
          <Fragment>
            {NotificationRecordTableFactory(records, notificationColumns)}
          </Fragment>
        );
      default:
        return <Fragment />;
    }
  };

  getTitleBasedOnDrawerContent = (): React.ReactNode => {
    const { activeDrawerContent } = this.state;
    switch (activeDrawerContent) {
      case "audits":
        return "Transfer Request Logs";
      case "notifierRecords":
        return "Notification Records";
      default:
        return "Entity";
    }
  };

  toggleModal = (): void => {
    this.setState({
      showCancelModal: !this.state.showCancelModal,
    });
  };

  handleReasonSelect = (reason: string) => {
    this.setState({ commentRequired: reason === '6' }) // 6 = other reasons
  }

  handleFileUploadChange = (field: string, combinedInfo: string): void => {
    const [url] = combinedInfo.split("@@@");
    this.setState((prevState) => ({
      ...prevState,
      [field]: url
    }));
  }

  handleNoteUpdate = (updatedNotes: Note[]) => {
    this.setState({ notes: updatedNotes });
  };

  toggleFollowUpModal = (): void => {
    this.setState((prev) => {
      const opening = !prev.showFollowUpModal;
      if (opening) this.loadFollowUpTemplates();
      return { showFollowUpModal: opening };
    });
  };

  loadFollowUpTemplates = async (): Promise<void> => {
    this.setState({ isFollowUpTemplatesLoading: true, followUpTemplates: [], followUpTemplateId: "" });
    try {
      const response: AxiosResponse = await request({
        query: GET_TEMPLATES_BY_CATEGORY_NAME,
        variables: { templateCategoryName: "Transfer In - Follow Up" },
      });
      const templates = response.data.data.templates ?? [];
      this.setState({ followUpTemplates: templates });
    } catch (e) {
      Sentry.captureException(e);
      message.error(`Failed to load follow-up templates. - ${generateDisplayErrorMessage(e)}`);
    } finally {
      this.setState({ isFollowUpTemplatesLoading: false });
    }
  };

  handleFollowUp = async (): Promise<void> => {
    const { transferRequest, followUpTemplateId } = this.state;
    if (!transferRequest) return;

    if (!followUpTemplateId) {
      message.warning("Please select a follow-up template.");
      return;
    }

    this.setState({ isFollowUpLoading: true });
    try {
      const response: AxiosResponse = await request({
        query: FOLLOW_UP_TRANSFER_REQUEST,
        variables: { transferRequestId: transferRequest.id, templateId: followUpTemplateId },
      });

      const { errors } = response.data.data.followUpTransferRequest;
      if (errors?.length) {
        message.error(errors[0].description);
      } else {
        message.success("Follow-up email sent to trustee.");
        this.setState({ showFollowUpModal: false });
      }
    } catch (e) {
      Sentry.captureException(e);
      message.error(`Failed to send follow-up. Please try again. - ${generateDisplayErrorMessage(e)}`);
    } finally {
      this.setState({ isFollowUpLoading: false });
    }
  };

  render(): JSX.Element | null {
    const {
      id,
      isLoading,
      transferRequest,
      transferRequestReasons,
      currentRequestsToProcess,
      availableTransitionStates,
      selectedTransitionState,
      showReasonModal,
      isReasonLoading,
      commentRequired,
      showDrawer,
      showFollowUpModal,
      followUpTemplateId,
      followUpTemplates,
      isFollowUpLoading,
      isFollowUpTemplatesLoading,
    } = this.state;
    const reasons: SelectOptionsInterface[] = transferRequestReasons.map(
      (reason) => {
        return { id: reason.id, name: reason.reason.reasonDescription };
      },
    );

    const firstContributionDate = transferRequest?.firstContributionDate ? moment(
      transferRequest?.firstContributionDate,
      "YYYY-MM-DD HH:mm:ss",
    ).format("DD/MM/YYYY")
      : "n/a";

    const subscription = this.getSubscription();

    const noReason: boolean = reasons.length <= 1;

    const subscriptionsWithProofOfContribution =
      transferRequest?.customer?.customerSubscriptions?.filter(
        (subscription) => subscription.proofOfContributionDocumentUrl !== null,
      );

    const moveToNextLink = `/transfer-requests/${this.props.location?.state?.requests?.[currentRequestsToProcess + 1]?.id
      }`;

    const moveToPreviousLink = `/transfer-requests/${this.props.location?.state?.requests?.[currentRequestsToProcess - 1]?.id
      }`;

    const nextPreviousUI = (
      <>
        <br />
        <Row
          gutter={10}
          style={{
            float: "right",
            marginTop: "10px",
          }}
        >
          <Col>
            {this.state.currentRequestsToProcess > 0 && (
              <NavLink
                to={{
                  pathname: moveToPreviousLink,
                  state: {
                    type: "redemption",
                    requests: this.props?.location?.state?.requests ?? [],
                  },
                }}
              >
                <Tooltip placement="left" title={"Previous"}>
                  <LeftOutlined style={{ color: "#1D3764" }} />
                </Tooltip>
              </NavLink>
            )}
          </Col>

          <Col>
            {this.props?.location?.state?.requests &&
              this.state.currentRequestsToProcess <
              this.props?.location?.state?.requests?.length - 1 && (
                <NavLink
                  to={{
                    pathname: moveToNextLink,
                    state: {
                      type: "redemption",
                      requests: this.props?.location?.state?.requests ?? [],
                    },
                  }}
                >
                  <Tooltip placement="right" title={"Next"}>
                    <RightOutlined style={{ color: "#1D3764" }} />
                  </Tooltip>
                </NavLink>
              )}
          </Col>

          <Col>
            {this.props?.location?.state?.requests &&
              `${currentRequestsToProcess + 1} / ${this.props?.location?.state?.requests?.length
              }`}
          </Col>
        </Row>
      </>
    );

    return (
      <Spin spinning={isLoading}>
        <Card>
          <Fragment>
            {ModalCreator({
              title: "Update Customer First Contribution",
              okType: "primary",
              okText: "Update Contribution",
              showModal: this.state.showFirstContributionModal,
              isLoading: this.state.isLoading,
              onOkay: this.updateCustomerFirstContribution,
              toggleModal: this.toggleFirstContributionModal,
              inscription: FirstContributionUI({
                reference: this.contributionReference,
                initialContributionDate: subscription?.computedFirstContributionDate,
                contributionDocumentUrl:
                  subscription?.proofOfContributionDocumentUrl,
                handleFileUploadChange: this.handleFileUploadChange
              }),
            })}

            <Row>
              <Col span={24}>
                {transferRequest && (
                  <>
                    <Descriptions
                      title={<div>Details</div>}
                      extra={
                        <div>
                          {subscription && (
                            <Button
                              style={{
                                marginLeft: "5px",
                              }}
                              className="base-button"
                              onClick={this.toggleFirstContributionModal}
                            >
                              Edit First Contribution
                            </Button>
                          )}
                          <Button
                            style={{
                              marginLeft: "5px",
                            }}
                            className="base-button"
                            onClick={this.toggleDrawer("notifierRecords")}
                          >
                            Notifications
                          </Button>
                          <Button
                            style={{
                              marginLeft: "5px",
                            }}
                            className="base-button"
                            onClick={this.toggleDrawer("audits")}
                          >
                            Request History
                          </Button>{" "}
                          <NavLink to={`/transfer-requests/${id}/edit`}>
                            <Button
                              icon={<EditOutlined />}
                              className="base-button"
                            >
                              Edit
                            </Button>{" "}
                          </NavLink>
                          {nextPreviousUI}
                        </div>
                      }
                    ></Descriptions>
                    <div className="loan-request-details-stack">
                      <AntdCard title="Customer">
                        <Descriptions>
                          <Descriptions.Item
                            label={<b>Requestor Name</b>}
                            key="requestorName"
                          >
                            {transferRequest.customer ? (
                              <NavLink
                                to={`/customers/${transferRequest.customer.id}`}
                              >
                                {generateNameFromCustomer(
                                  transferRequest.customer,
                                  false,
                                )}
                              </NavLink>
                            ) : transferRequest.company ? (
                              <NavLink
                                to={`/companies/${transferRequest.company.id}`}
                              >
                                {transferRequest.company.name}
                              </NavLink>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>

                          <Descriptions.Item label={<b>Petra ID</b>} key="petraId">
                            {transferRequest.customer?.petraId ||
                              transferRequest.company?.petraId ||
                              "N/A"}
                          </Descriptions.Item>
                          <Descriptions.Item label={<b>ID Type</b>} key="idType">
                            {transferRequest.idType?.name || "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item label={<b>ID Number</b>} key="idNumber">
                            {transferRequest.idNumber
                              ? capitalizeFirstLetter(transferRequest.idNumber)
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item label={<b>ID</b>} key="idUrl">
                            {transferRequest.idUrl ? (
                              <a
                                target="_blank"
                                href={transferRequest.idUrl}
                                rel="noopener noreferrer"
                              >
                                Click To Open Image
                              </a>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>
                        </Descriptions>
                      </AntdCard>

                      <AntdCard
                        title={
                          <span>
                            Transfer Request{" "}
                            {transferRequest.transferStatus ? (
                              <Tag
                                color={getStateColor(
                                  transferRequest.transferStatus,
                                )}
                                style={{
                                  textTransform: "capitalize",
                                  marginLeft: "3px",
                                }}
                              >
                                {formatSnakeCase(
                                  transferRequest.transferStatus,
                                )}
                              </Tag>
                            ) : (
                              "N/A"
                            )}
                          </span>
                        }
                      >
                        <Descriptions>
                          <Descriptions.Item
                            label={<b>Transfer Notification Sent To</b>}
                            key="contactInformation"
                          >
                            {transferRequest.contactInformation || "N/A"}
                          </Descriptions.Item>
                          <Descriptions.Item
                            label={<b>Request Type</b>}
                            key="Category Type"
                          >
                            {transferRequest?.subCategory?.name}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Assigned To</b>}
                            key="Assigned To"
                          >
                            {transferRequest.user?.name}
                          </Descriptions.Item>
                          <Descriptions.Item label={<b>Amount</b>} key="amount">
                            {transferRequest.amount || "N/A"}
                          </Descriptions.Item>
                          <Descriptions.Item
                            label={<b>Request Reason</b>}
                            key="requestReason"
                            style={{ textTransform: "capitalize" }}
                          >
                            {transferRequest.transferRequestReason?.reason
                              .reasonDescription || "N/A"}
                          </Descriptions.Item>
                          <Descriptions.Item
                            label={<b>Redemption Request State</b>}
                            key="redemptionRequestState"
                            style={{ textTransform: "capitalize" }}
                          >
                            {transferRequest.redemptionRequest?.state ? (
                              <Link
                                to={`/redemption-requests/${transferRequest.redemptionRequest.id}`}
                              >
                                <Tag
                                  color={getStateColor(
                                    transferRequest.redemptionRequest.state,
                                  )}
                                  style={{ textTransform: "capitalize" }}
                                >
                                  {transferRequest.redemptionRequest.state}
                                </Tag>
                              </Link>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>
                          <Descriptions.Item
                            label={<b>Transfer Type</b>}
                            key="transferType"
                          >
                            {transferTypes[transferRequest.transferType] ||
                              "N/A"}
                          </Descriptions.Item>
                          <Descriptions.Item label={<b>Trustee</b>} key="trustee">
                            {transferRequest.trusteeScheme?.trustee.name ||
                              "N/A"}
                          </Descriptions.Item>
                          <Descriptions.Item
                            label={<b>Trustee Scheme</b>}
                            key="trusteeScheme"
                          >
                            {transferRequest.trusteeScheme?.name || "N/A"}
                          </Descriptions.Item>
                          <Descriptions.Item
                            label={<b>Authorization Form</b>}
                            key="authorizationForm"
                          >
                            {transferRequest.authorizationFormUrl ? (
                              <a
                                target="_blank"
                                href={transferRequest.authorizationFormUrl}
                                rel="noopener noreferrer"
                              >
                                View File
                              </a>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Identification Form</b>}
                            key="identificationForm"
                          >
                            {transferRequest.identificationFormUrl ? (
                              <a
                                target="_blank"
                                href={transferRequest.identificationFormUrl}
                                rel="noopener noreferrer"
                              >
                                View File
                              </a>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>
                          <Descriptions.Item
                            label={<b>From Product</b>}
                            key="fromProduct"
                          >
                            {transferRequest.fromProduct?.name || "N/A"}
                          </Descriptions.Item>
                          <Descriptions.Item label={<b>To Product</b>} key="toProduct">
                            {transferRequest.toProduct?.name || "N/A"}
                          </Descriptions.Item>
                          <Descriptions.Item
                            label={<b>From Company</b>}
                            key="fromCompany"
                          >
                            {transferRequest.fromCompany ? (
                              <NavLink
                                to={`/companies/${transferRequest.fromCompany.id}`}
                              >
                                {transferRequest.fromCompany.name}
                              </NavLink>
                            ) : transferRequest.fromCompanyName ? (
                              <NavLink to="#">
                                {transferRequest.fromCompanyName}
                              </NavLink>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>
                          <Descriptions.Item label={<b>To Company</b>} key="toCompany">
                            {transferRequest.toCompany ? (
                              <NavLink
                                to={`/companies/${transferRequest.toCompany.id}`}
                              >
                                {transferRequest.toCompany.name}
                              </NavLink>
                            ) : transferRequest.toCompanyName ? (
                              <NavLink to="#">
                                {transferRequest.toCompanyName}
                              </NavLink>
                            ) : (
                              "N/A"
                            )}
                          </Descriptions.Item>
                          <Descriptions.Item label={<b>Proof Of Contribution Document</b>} key="proofOfContributionDocument">
                              {transferRequest.proofOfContributionDocumentUrl ? (
                            <a
                              href={
                                transferRequest.proofOfContributionDocumentUrl
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Proof Of Contribution Document
                            </a>) : ("N/A")}
                          </Descriptions.Item>
                        </Descriptions>
                      </AntdCard>
                      <AntdCard title="Dates and Timelines">
                        <Descriptions>
                          <Descriptions.Item
                            label={<b>Request Date</b>}
                            key="requestDate"
                          >
                            {transferRequest.requestDate
                              ? moment(
                                  transferRequest.requestDate,
                                  "YYYY-MM-DD HH:mm:ss",
                                ).format("DD/MM/YYYY")
                              : "N/A"}
                          </Descriptions.Item>
                          <Descriptions.Item label={<b>Created At</b>} key="createdAt">
                            {transferRequest.createdAt
                              ? moment(
                                  transferRequest.createdAt,
                                  "YYYY-MM-DD HH:mm:ss",
                                ).format("llll")
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item label={<b>Updated At</b>} key="updatedAt">
                            {transferRequest.updatedAt
                              ? moment(
                                  transferRequest.updatedAt,
                                  "YYYY-MM-DD HH:mm:ss",
                                ).format("llll")
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item label={<b>Due Date</b>} key="dueDate">
                            {transferRequest.dueDate
                              ? moment(
                                  transferRequest.dueDate,
                                  "YYYY-MM-DD HH:mm:ss",
                                ).format("llll")
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Awaiting Funds Date</b>}
                            key="awaitingFundsDate"
                            style={{ textTransform: "capitalize" }}
                          >
                            {transferRequest.awaitingFundsDate
                              ? moment(
                                  transferRequest.awaitingFundsDate,
                                  "YYYY-MM-DD HH:mm:ss",
                                ).format("DD/MM/YYYY")
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Awaiting Funds Counted Days</b>}
                            key="awaitingFundsDaysCount"
                            style={{ textTransform: "capitalize" }}
                          >
                            {transferRequest.awaitingFundsDate &&
                            transferRequest.transferStatus === "awaiting_funds"
                              ? transferRequest.awaitingFundsDaysCount || "N/A"
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>Processing Days</b>}
                            key="processingDays"
                          >
                            {transferRequest.processingDays &&
                            transferRequest.transferStatus !== "completed" &&
                            transferRequest.transferStatus !== "cancelled"
                              ? `${transferRequest.processingDays} day(s)`
                              : "N/A"}
                          </Descriptions.Item>

                          <Descriptions.Item
                            label={<b>First Contribution Date</b>}
                            key="firstContributionDate"
                          >
                            {transferRequest.proofOfContributionDocumentUrl
                              ? firstContributionDate || "N/A"
                              : "N/A"}
                          </Descriptions.Item>
                        </Descriptions>
                      </AntdCard>
                    </div>
                  </>
                )}
              </Col>
            </Row>

            {
              <>
                <div
                  style={{
                    marginTop: "25px",
                    display: "flex",
                    justifyContent: "start",
                  }}
                >
                  {availableTransitionStates?.map((transition, i) => (
                    <Button
                      onClick={() => this.requestForReason(transition.state)}
                      className={this.generateButtonColor(transition.state)}
                      id={`transition-btn-${i}`}
                      key={i}
                      style={{ marginRight: '5px' }}
                      disabled={isReasonLoading}
                    >
                      {generateStateButtonText(transition.event)}
                    </Button>
                  ))}
                  {(() => {
                    const storedUser = localStorage.getItem("user");
                    const currentUser = storedUser ? JSON.parse(storedUser) : null;
                    const { canAccess } = checkPermission(currentUser, entitlements.TransferRequests?.edit);
                    const daysElapsed = transferRequest?.awaitingFundsDaysCount ?? 0;
                    const showFollowUp =
                      canAccess &&
                      transferRequest?.transferStatus === "awaiting_funds" &&
                      daysElapsed > 30;
                    return showFollowUp ? (
                      <Button
                        className="base-button"
                        style={{ marginRight: '5px' }}
                        onClick={this.toggleFollowUpModal}
                      >
                        Follow-Up
                      </Button>
                    ) : null;
                  })()}
                </div>


                {ModalCreator({
                  title: noReason ? "" : "Select Redemption Reason",
                  okType: "primary",
                  okText: noReason ? "Comment" : "Select Reason",
                  showModal: showReasonModal,
                  onOkay: this.saveStateReason,
                  toggleModal: this.toggleShowReasonModal,
                  isLoading: isReasonLoading,
                  okayButtonProps: {
                    disabled: isReasonLoading,
                  },
                  inscription: RequestReasonUI({
                    reference: this.reasonReference,
                    isLoading: isReasonLoading,
                    reasons,
                    selectedTransitionState,
                    handleReasonSelect: this.handleReasonSelect,
                    handleFileUploadChange: this.handleFileUploadChange,
                    availableTransitionStates,
                    commentRequired,
                    type: 'transfer',
                    request: transferRequest
                  }),
                })}

                <Modal
                  title="Send Follow-Up to Trustee"
                  visible={showFollowUpModal}
                  onOk={this.handleFollowUp}
                  onCancel={this.toggleFollowUpModal}
                  okText="Send Follow-Up"
                  confirmLoading={isFollowUpLoading}
                  okButtonProps={{ disabled: !followUpTemplateId }}
                >
                  <p>Select the follow-up template to send to the trustee:</p>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select a template"
                    loading={isFollowUpTemplatesLoading}
                    value={followUpTemplateId || undefined}
                    onChange={(value: string) => this.setState({ followUpTemplateId: value })}
                  >
                    {followUpTemplates.map((t) => (
                      <Select.Option key={t.id} value={t.id}>
                        {t.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Modal>
              </>
            }
          </Fragment>
        </Card>

        {transferRequest && transferRequest.trusteeScheme && (
          <Collapse style={{ marginTop: "20px" }}>
            <Panel
              header={
                <span>
                  Transfer Payout Account{" "}
                  <Badge
                    count={transferRequest?.payoutAccount ? 1 : 0}
                    style={tabPaneStyle}
                  />
                </span>
              }
              key={"TransferPayoutAccount"}
            >
              {transferRequest?.payoutAccount && (
                <Col span={8} style={{ marginTop: "20px" }}>
                  <Card
                    title={<b>{"Payout Account"}</b>}
                    size="small"
                    style={{
                      width: 300,
                      borderTop: "4px solid rgb(29, 55, 100)",
                      borderRadius: "4px",
                    }}
                  >
                    <PaymentAccountCard
                      paymentAccount={transferRequest?.payoutAccount}
                    />
                  </Card>
                </Col>
              )}
            </Panel>
            <Panel header="Trustee Contact Person" key="Trustee Contact Person">
              <Descriptions.Item
                label={<b>Trustee Contact Person</b>}
                key="Trustee Scheme"
              >
                {transferRequest.trusteeScheme?.trusteeContactPerson && (
                  <CardSection
                    headingText="Name"
                    data={generateNameFromCustomer({
                      firstName:
                        transferRequest.trusteeScheme?.trusteeContactPerson
                          .firstName,
                      middleName:
                        transferRequest.trusteeScheme?.trusteeContactPerson
                          .middleName,
                      lastName:
                        transferRequest.trusteeScheme?.trusteeContactPerson
                          .lastName,
                    } as unknown as Customer)}
                  />
                )}

                {transferRequest.trusteeScheme?.trusteeContactPerson?.email && (
                  <CardSection
                    headingText="Email"
                    data={
                      transferRequest.trusteeScheme?.trusteeContactPerson
                        ?.email ?? ""
                    }
                  />
                )}

                {transferRequest.trusteeScheme?.trusteeContactPerson
                  ?.phoneNumber && (
                    <CardSection
                      headingText="Phone Number"
                      data={
                        transferRequest.trusteeScheme?.trusteeContactPerson
                          ?.phoneNumber ?? ""
                      }
                    />
                  )}
              </Descriptions.Item>
            </Panel>
          </Collapse>
        )}

        <Divider className="view-company-ant-divider" />

        <Collapse>
          {transferRequest &&
            transferRequest.ticketLinks &&
            transferRequest?.ticketLinks?.length > 0 && (
              <Panel
                header={
                  <span>
                    Ticket Links{" "}
                    <Badge
                      count={transferRequest.ticketLinks.length}
                      style={tabPaneStyle}
                    />
                  </span>
                }
                key={"TicketLinks"}
              >
                <AttachmentsDisplay
                  attachmentName="Ticket Link"
                  attachments={transferRequest?.ticketLinks.map((link) => {
                    return {
                      id: link,
                      url: link ?? "",
                      key: link,
                      displayName: link,
                    };
                  })}
                />
              </Panel>
            )}

          {transferRequest &&
            subscriptionsWithProofOfContribution &&
            subscriptionsWithProofOfContribution?.length > 0 && (
              <Panel
                header={
                  <span>
                    Proof of Contribution Attachements{" "}
                    <Badge
                      count={subscriptionsWithProofOfContribution.length}
                      style={tabPaneStyle}
                    />
                  </span>
                }
                key={"ProofOfContributionAttachments"}
              >
                <AttachmentsDisplay
                  attachmentName="Proof"
                  attachments={subscriptionsWithProofOfContribution.map(
                    (subscriptionItem) => {
                      return {
                        id: subscriptionItem.id,
                        url:
                          subscriptionItem.proofOfContributionDocumentUrl ?? "",
                        key: subscriptionItem.id,
                        displayName: subscriptionItem?.product?.name,
                      };
                    },
                  )}
                />
              </Panel>
            )}
          <Panel
            header={
              <span>
                Attachments{" "}
                <Badge
                  count={
                    this.state.transferRequest?.transferRequestAttachments
                      ?.length || 0
                  }
                  style={tabPaneStyle}
                />
              </span>
            }
            key={"Attachments"}
          >
            <AttachmentsSection
              reference={this.reference}
              createAttachment={this.createAttachment}
              attachments={this.state.attachments}
              showForm={true}
              handleRemove={this.unassignAttachmentToTransferRequest}
            />
          </Panel>
          <Panel
            header={
              <span>
                Notes{" "}
                <Badge
                  count={
                    this.state.transferRequest?.transferRequestNotes?.length ||
                    0
                  }
                  style={tabPaneStyle}
                />
              </span>
            }
            key={"Notes"}
          >
            {transferRequest && (
              <NotesSection
                showHeading={true}
                reference={this.reference}
                createNote={this.createNote}
                notes={this.state.notes}
                showForm={true}
                entity="TransferRequest"
                onNoteUpdate={this.handleNoteUpdate}
              />
            )}
          </Panel>
        </Collapse>
        <Drawer
          title={this.getTitleBasedOnDrawerContent()}
          width={1200}
          onClose={this.toggleModal}
          visible={showDrawer}
          bodyStyle={{ paddingBottom: 80 }}
          maskClosable={false}
        >
          {this.getDrawerContent()}
        </Drawer>
        {this.state.showCancelModal &&
          ModalCreator({
            title: "Cancel",
            okType: "primary",
            okText: "Yes",
            width: 400,
            showModal: true,
            onOkay: this.toggleDrawer(""),
            toggleModal: this.toggleModal,
            showFooter: true,
            inscription: `Are you sure you want to cancel ${spaceCamel(
              this.state.activeDrawerContent,
            ).toLocaleLowerCase()} ?`,
          })}
      </Spin>
    );
  }
}
